import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  Fab,
  Zoom,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@mui/material';
import {
  Add,
  PersonAdd,
  EventNote,
  Assignment,
  Description,
  Campaign,
  Work,
  Stars,
  AccessTime,
  CalendarMonth
} from '@mui/icons-material';

interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  variant?: 'contained' | 'outlined' | 'text';
}

interface QuickActionsBarProps {
  actions: QuickAction[];
  variant?: 'horizontal' | 'speedDial' | 'fab';
  position?: 'static' | 'fixed';
  fixedPosition?: {
    bottom?: number;
    right?: number;
    left?: number;
    top?: number;
  };
}

const QuickActionsBar: React.FC<QuickActionsBarProps> = ({
  actions,
  variant = 'horizontal',
  position = 'static',
  fixedPosition = { bottom: 24, right: 24 }
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [speedDialOpen, setSpeedDialOpen] = React.useState(false);

  if (variant === 'speedDial' && position === 'fixed') {
    return (
      <SpeedDial
        ariaLabel="Quick Actions"
        sx={{
          position: 'fixed',
          ...fixedPosition
        }}
        icon={<SpeedDialIcon />}
        onClose={() => setSpeedDialOpen(false)}
        onOpen={() => setSpeedDialOpen(true)}
        open={speedDialOpen}
        direction="up"
      >
        {actions.map((action) => (
          <SpeedDialAction
            key={action.id}
            icon={action.icon}
            tooltipTitle={action.label}
            tooltipOpen
            onClick={() => {
              action.onClick();
              setSpeedDialOpen(false);
            }}
            sx={{
              '& .MuiSpeedDialAction-fab': {
                backgroundColor: action.color
                  ? theme.palette[action.color].main
                  : theme.palette.primary.main,
                color: '#fff',
                '&:hover': {
                  backgroundColor: action.color
                    ? theme.palette[action.color].dark
                    : theme.palette.primary.dark
                }
              }
            }}
          />
        ))}
      </SpeedDial>
    );
  }

  if (variant === 'fab') {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: 'center',
          ...(position === 'fixed' && {
            position: 'fixed',
            ...fixedPosition
          })
        }}
      >
        {actions.map((action, index) => (
          <Zoom
            key={action.id}
            in={true}
            style={{ transitionDelay: `${index * 50}ms` }}
          >
            <Tooltip title={action.label}>
              <Fab
                size="medium"
                color={action.color || 'primary'}
                onClick={action.onClick}
                sx={{
                  boxShadow: 3
                }}
              >
                {action.icon}
              </Fab>
            </Tooltip>
          </Zoom>
        ))}
      </Box>
    );
  }

  // Horizontal variant (default)
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        flexWrap: 'wrap',
        p: 2,
        borderRadius: 2,
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`,
        ...(position === 'fixed' && {
          position: 'fixed',
          ...fixedPosition,
          zIndex: theme.zIndex.fab
        })
      }}
    >
      {actions.map((action) => (
        <Button
          key={action.id}
          variant={action.variant || 'contained'}
          color={action.color || 'primary'}
          startIcon={action.icon}
          onClick={action.onClick}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 1.5,
            px: 2,
            py: 1,
            minWidth: 'auto',
            boxShadow: action.variant === 'contained' ? 2 : 0,
            '&:hover': {
              boxShadow: action.variant === 'contained' ? 4 : 0
            }
          }}
        >
          {action.label}
        </Button>
      ))}
    </Box>
  );
};

// Pre-defined action sets for different roles
export const getHRQuickActions = (handlers: {
  onAddEmployee?: () => void;
  onPostJob?: () => void;
  onApproveLeaves?: () => void;
  onPostAnnouncement?: () => void;
  onGiveRecognition?: () => void;
  onViewAttendance?: () => void;
}): QuickAction[] => [
  {
    id: 'add-employee',
    label: 'Add Employee',
    icon: <PersonAdd />,
    onClick: handlers.onAddEmployee || (() => {}),
    color: 'primary'
  },
  {
    id: 'post-job',
    label: 'Post Job',
    icon: <Work />,
    onClick: handlers.onPostJob || (() => {}),
    color: 'success'
  },
  {
    id: 'approve-leaves',
    label: 'Approve Leaves',
    icon: <EventNote />,
    onClick: handlers.onApproveLeaves || (() => {}),
    color: 'warning'
  },
  {
    id: 'post-announcement',
    label: 'Post Announcement',
    icon: <Campaign />,
    onClick: handlers.onPostAnnouncement || (() => {}),
    color: 'info'
  },
  {
    id: 'give-recognition',
    label: 'Give Recognition',
    icon: <Stars />,
    onClick: handlers.onGiveRecognition || (() => {}),
    color: 'secondary'
  }
];

export const getEmployeeQuickActions = (handlers: {
  onLogTime?: () => void;
  onApplyLeave?: () => void;
  onViewPayslip?: () => void;
  onViewCalendar?: () => void;
}): QuickAction[] => [
  {
    id: 'log-time',
    label: 'Log Time',
    icon: <AccessTime />,
    onClick: handlers.onLogTime || (() => {}),
    color: 'primary'
  },
  {
    id: 'apply-leave',
    label: 'Apply Leave',
    icon: <EventNote />,
    onClick: handlers.onApplyLeave || (() => {}),
    color: 'warning'
  },
  {
    id: 'view-payslip',
    label: 'View Payslip',
    icon: <Description />,
    onClick: handlers.onViewPayslip || (() => {}),
    color: 'success'
  },
  {
    id: 'view-calendar',
    label: 'Calendar',
    icon: <CalendarMonth />,
    onClick: handlers.onViewCalendar || (() => {}),
    color: 'info'
  }
];

export const getAdminQuickActions = (handlers: {
  onAddUser?: () => void;
  onManageRoles?: () => void;
  onViewLogs?: () => void;
  onSystemSettings?: () => void;
}): QuickAction[] => [
  {
    id: 'add-user',
    label: 'Add User',
    icon: <PersonAdd />,
    onClick: handlers.onAddUser || (() => {}),
    color: 'primary'
  },
  {
    id: 'manage-roles',
    label: 'Manage Roles',
    icon: <Assignment />,
    onClick: handlers.onManageRoles || (() => {}),
    color: 'secondary'
  }
];

export default QuickActionsBar;
